import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/useAuthStore';
import { ensureI18nMessagesLoaded } from '@/composables/useI18n';
import { ref } from 'vue';
import { logAuthDebug, warnAuthDebug } from '@/utils/authDebug';
import { recoverFromChunkLoadError } from '@/utils/chunkLoadRecovery';

const Login = () => import('@/pages/Login.vue');
const Register = () => import('@/pages/Register.vue');
const VerifyEmail = () => import('@/pages/VerifyEmail.vue');
const Open = () => import('@/pages/Open.vue');
const Dashboard = () => import('@/pages/Dashboard.vue');
const Onboarding = () => import('@/pages/Onboarding.vue');
const OnboardingFlow = () => import('@/pages/OnboardingFlow.vue');
const Tasks = () => import('@/pages/Tasks.vue');
const Settings = () => import('@/pages/Settings.vue');
const Help = () => import('@/pages/Help.vue');
const Contacts = () => import('@/pages/Contacts.vue');
const CommunicationInboxView = () => import('@/pages/CommunicationInboxView.vue');
const AdminHome = () => import('@/pages/AdminHome.vue');
const AdminNoAccess = () => import('@/pages/AdminNoAccess.vue');

export const initialNavigationPending = ref(true);

export const routes = [
  { path: '/login', name: 'login', component: Login, meta: { public: true } },
  { path: '/register', name: 'register', component: Register, meta: { public: true } },
  { path: '/verify-email', name: 'verifyEmail', component: VerifyEmail, meta: { public: true } },
  { path: '/impressum', name: 'impressum', component: () => import('@/pages/Impressum.vue'), meta: { public: true } },
  { path: '/datenschutz', name: 'datenschutz', component: () => import('@/pages/Datenschutz.vue'), meta: { public: true } },
  { path: '/agb', name: 'agb', component: () => import('@/pages/Agb.vue'), meta: { public: true } },
  { path: '/open', name: 'open', component: Open, meta: { public: true } },
  { path: '/paywall', name: 'paywall', component: () => import('@/pages/Paywall.vue'), meta: { paywall: true } },
  { path: '/', redirect: { name: 'dashboard' } },
  { path: '/dashboard', name: 'dashboard', component: Dashboard },
  { path: '/onboarding', name: 'onboarding', component: Onboarding },
  { path: '/willkommen', name: 'onboardingFlow', component: OnboardingFlow },
  { path: '/tasks', name: 'tasks', component: Tasks },
  { path: '/settings', name: 'settings', component: Settings },
  { path: '/verwaltung/hilfe', name: 'adminHelp', component: Help },
  { path: '/profile', name: 'profile', component: () => import('@/pages/Profile.vue') },
  { path: '/family', name: 'family', component: () => import('@/pages/Family.vue') },
  { path: '/children', name: 'children', component: () => import('@/pages/Children.vue') },
  { path: '/birthdays', name: 'birthdays', component: () => import('@/pages/Children.vue') },
  { path: '/areas', name: 'areas', component: () => import('@/pages/Areas.vue') },
  { path: '/contacts', name: 'contacts', component: Contacts },
  { path: '/admin', name: 'admin', component: AdminHome, meta: { adminEntry: true } },
  { path: '/admin/', redirect: { name: 'admin' } },
  { path: '/system', redirect: { name: 'admin' } },
  { path: '/admin/no-access', name: 'adminNoAccess', component: AdminNoAccess },
  { path: '/admin/communication', name: 'adminCommunication', component: CommunicationInboxView },
  // Legacy alias for existing deep links and dashboard routes.
  { path: '/admin/emails', name: 'adminEmails', component: CommunicationInboxView },
  { path: '/admin/system', name: 'adminSystemLanding', component: () => import('@/pages/AdminSystemLanding.vue'), meta: { adminOnly: true } },
  { path: '/admin/system/overview', name: 'adminSystemOverview', component: () => import('@/pages/AdminOverview.vue'), meta: { adminOnly: true } },
  { path: '/admin/system/users', name: 'adminSystemUsers', component: () => import('@/pages/AdminUsers.vue'), meta: { adminOnly: true } },
  { path: '/admin/system/errors', name: 'adminSystemErrors', component: () => import('@/pages/AdminErrors.vue'), meta: { adminOnly: true } },
  { path: '/admin/system/audit', name: 'adminSystemAudit', component: () => import('@/pages/AdminAudit.vue'), meta: { adminOnly: true } },
  { path: '/admin/system/today-in-focus', name: 'adminSystemTodayInFocus', component: () => import('@/pages/AdminTodayInFocus.vue'), meta: { adminOnly: true } },
  { path: '/admin/system/milestones', name: 'adminSystemMilestones', component: () => import('@/pages/AdminMilestones.vue'), meta: { adminOnly: true } },
  { path: '/admin/system/workflows', name: 'adminSystemWorkflows', component: () => import('@/pages/AdminWorkflows.vue'), meta: { adminOnly: true } },
  { path: '/admin/system/insights', name: 'adminSystemInsights', component: () => import('@/pages/AdminInsights.vue'), meta: { adminOnly: true } },
  { path: '/admin/system/sources', name: 'adminSystemSources', component: () => import('@/pages/AdminSources.vue'), meta: { adminOnly: true } },
  { path: '/admin/system/gift-feeds', name: 'adminSystemGiftFeeds', component: () => import('@/pages/AdminGiftFeeds.vue'), meta: { adminOnly: true } },
  { path: '/admin/system/feedback', name: 'adminSystemFeedback', component: () => import('@/pages/AdminFeedback.vue'), meta: { adminOnly: true } },
  { path: '/events', name: 'events', component: () => import('@/pages/Events.vue') },
  { path: '/calendar', name: 'calendar', component: () => import('@/pages/Events.vue') },
  { path: '/friends', name: 'friends', component: () => import('@/pages/Friends.vue') },
  { path: '/needs', redirect: { name: 'tasks', query: { status: 'need' } } },
  { path: '/media', name: 'media', component: () => import('@/pages/MediaHub.vue') },
  { path: '/documents', name: 'documents', component: () => import('@/pages/MediaHub.vue') },
  { path: '/media/:memberType/:memberId', name: 'mediaMember', component: () => import('@/pages/MediaMember.vue') },
  { path: '/gifts', name: 'gifts', component: () => import('@/pages/Gifts.vue') },
  { path: '/recurring-tasks', redirect: { name: 'tasks' } },
  { path: '/scoring-settings', name: 'scoringSettings', component: () => import('@/pages/ScoringSettings.vue') },
  { path: '/scoring-stats', name: 'scoringStats', component: () => import('@/pages/ScoringStats.vue') },
  { path: '/:pathMatch(.*)*', name: 'notFound', component: () => import('@/pages/NotFound.vue'), meta: { public: true } }
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

const redirectToLogin = (to) => ({
  name: 'login',
  query: { returnTo: to.fullPath }
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  const isPublicRoute = to.meta.public === true;
  logAuthDebug('guard.enter', { to: to.fullPath, isPublicRoute });

  if (isPublicRoute) {
    initialNavigationPending.value = false;
    const isLoginRoute = to.name === 'login';
    if (!isLoginRoute && !auth.loginInProgress && !auth.authResolved && !auth.initPromise) {
      auth.initAuth({ background: true }).catch((error) => {
        warnAuthDebug('guard.public.init.fail', { to: to.fullPath, message: error?.message || 'unknown_error' });
      });
    }

    if (isLoginRoute && auth.authStatus === 'authenticated' && auth.user) {
      logAuthDebug('guard.public.redirect', { from: to.fullPath, reason: 'already_authenticated' });
      return { name: 'dashboard' };
    }

    logAuthDebug('guard.public.allow', { to: to.fullPath });
    return true;
  }

  await ensureI18nMessagesLoaded();
  await auth.initAuth();

  if (auth.authStatus !== 'authenticated' || !auth.token) {
    initialNavigationPending.value = false;
    logAuthDebug('guard.protected.redirect', { to: to.fullPath, reason: 'missing_auth' });
    return redirectToLogin(to);
  }

  // Full lockout once the trial has ended (unless paid or an internal account).
  // Whitelisted users get trialStatus.expired === false, so they're unaffected.
  // ?checkout=success gets a grace pass so a slightly-late webhook doesn't bounce
  // a paying user back to the paywall.
  const lockedOut =
    auth.trialStatus?.enforcePaywall === true &&
    auth.trialStatus?.expired === true &&
    !auth.user?.isPaid &&
    !auth.user?.isInternalTestAccount;
  if (lockedOut && to.name !== 'paywall' && to.query.checkout !== 'success') {
    initialNavigationPending.value = false;
    return { name: 'paywall' };
  }
  if (!lockedOut && to.name === 'paywall') {
    initialNavigationPending.value = false;
    return { name: 'dashboard' };
  }

  if (to.meta.adminEntry || to.meta.adminOnly) {
    const hasAdminAccess = await auth.checkAdminAccess();
    if (!hasAdminAccess) {
      initialNavigationPending.value = false;
      logAuthDebug('guard.admin.redirect', { to: to.fullPath, reason: 'missing_admin_access' });
      if (to.name === 'adminNoAccess') {
        return true;
      }
      return { name: 'adminNoAccess' };
    }
  }

  initialNavigationPending.value = false;
  logAuthDebug('guard.allow', { to: to.fullPath });
  return true;
});

router.afterEach(() => {
  initialNavigationPending.value = false;
});

router.onError((error) => {
  initialNavigationPending.value = false;
  void recoverFromChunkLoadError(error, 'router').then((recovered) => {
    if (!recovered) {
      console.warn('mentalLoad: Router-Chunk-Recovery fehlgeschlagen.', error);
    }
  });
});

export default router;
